import { addScript, fetchWithRetry } from '@tunein/web-utils';
import {
  recaptchaSecret,
  recaptchaSiteKey,
  recaptchaV2Secret,
  recaptchaVerifyEndpoint,
} from '../../constants/auth/recaptcha';

function generateVerifyUrl(token, ipAddress, secret = recaptchaSecret) {
  const url = new URL(recaptchaVerifyEndpoint);

  const params = new URLSearchParams({
    secret,
    response: token,
  });

  if (ipAddress) {
    params.set('remoteip', ipAddress);
  }

  url.search = params.toString();

  return url.toString();
}

function readyCallback(action, resolve) {
  return async () => {
    const token = await window.grecaptcha.execute(recaptchaSiteKey, { action });
    resolve(token);
  };
}

const RECAPTCHA_V2_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
let recaptchaV2ScriptPromise = null;

export async function loadRecaptchaV2Script() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve();
  }

  if (recaptchaV2ScriptPromise) {
    return recaptchaV2ScriptPromise;
  }

  if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
    recaptchaV2ScriptPromise = Promise.resolve();
    return recaptchaV2ScriptPromise;
  }

  try {
    recaptchaV2ScriptPromise = addScript({
      src: RECAPTCHA_V2_SCRIPT_SRC,
      async: true,
      attributes: { defer: 'true' },
    });

    await recaptchaV2ScriptPromise;
  } catch (error) {
    recaptchaV2ScriptPromise = null;

    throw error;
  }
}

export function getRecaptchaToken(action, onError) {
  if (!window.grecaptcha) {
    onError({
      message: 'Recaptcha V3: grecaptcha not defined',
      context: { action },
    });
    return null;
  }

  return new Promise((resolve) => {
    try {
      window.grecaptcha.ready(readyCallback(action, resolve));
    } catch (e) {
      onError({
        message: 'Recaptcha V3: error fetching token',
        context: { action },
      });
      resolve(null);
    }
  });
}

export async function fetchRecaptchaScore(originalAction, token, ipAddress) {
  const verifyUrl = generateVerifyUrl(token, ipAddress);
  const {
    success,
    action,
    score,
    'error-codes': errorCodes,
  } = await fetchWithRetry(verifyUrl, { method: 'POST' });

  if (success && action === originalAction) {
    return score;
  }

  const errorCodeString = errorCodes?.toString() || 'no error codes available';
  throw new Error(`Recaptcha V3: error verifying token (${errorCodeString})`);
}

export async function verifyRecaptchaV2Token(token, ipAddress) {
  const verifyUrl = generateVerifyUrl(token, ipAddress, recaptchaV2Secret);
  const {
    success,
    'error-codes': errorCodes,
    challenge_ts: challengeTimestamp,
    hostname,
  } = await fetchWithRetry(verifyUrl, { method: 'POST' });

  const normalizedErrorCodes = Array.isArray(errorCodes)
    ? errorCodes
    : errorCodes
      ? [errorCodes]
      : [];

  return {
    success: Boolean(success),
    errorCodes: normalizedErrorCodes,
    challengeTimestamp,
    hostname,
  };
}
